import React, { useEffect, useState, useMemo } from 'react';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import { toast } from 'react-toastify';
import { ToastContainer } from 'react-toastify';
import { FaShieldAlt, FaCalendarAlt } from "react-icons/fa";
import { BsPeopleFill } from "react-icons/bs";
import { RiDiscountPercentFill } from "react-icons/ri";
import { LuTicketPercent } from "react-icons/lu";

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import { propTypes } from '../../../util/types';
import {
  isErrorNoPermissionForInitiateTransactions,
  isErrorNoPermissionForUserPendingApproval,
  isTooManyRequestsError,
} from '../../../util/errors';

import {
  Form,
  PrimaryButton,
  FieldTextInput,
  IconInquiry,
  Heading,
  NamedLink,
  FieldDateRangePicker,
} from '../../../components';

import {
  isDayBlockedFn,
  isOutsideRangeFn,
  handleMonthClick,
  combineConsecutiveTimeSlots,
  getStartAndEndOnTimeZone,
  getInclusiveEndDate,
  isBlockedBetween,
  getAllTimeSlots,
} from '../../../components/OrderPanel/BookingDatesForm/BookingDatesForm';
import { getStartOf, timeOfDayFromTimeZoneToLocal, isSameDay } from '../../../util/dates';


import css from './InquiryForm.module.css';
import { NO_ACCESS_PAGE_INITIATE_TRANSACTIONS } from '../../../util/urlHelpers';
import { IoInformationCircleOutline } from 'react-icons/io5';

const ErrorMessage = props => {
  const { error } = props;
  const userPendingApproval = isErrorNoPermissionForUserPendingApproval(error);
  const userHasNoTransactionRights = isErrorNoPermissionForInitiateTransactions(error);

  // No transaction process attached to listing
  return error ? (
    <p className={css.error}>
      {error.message === 'No transaction process attached to listing' ? (
        <FormattedMessage id="InquiryForm.sendInquiryErrorNoProcess" />
      ) : isTooManyRequestsError(error) ? (
        <FormattedMessage id="InquiryForm.tooManyRequestsError" />
      ) : userPendingApproval ? (
        <FormattedMessage id="InquiryForm.userPendingApprovalError" />
      ) : userHasNoTransactionRights ? (
        <FormattedMessage
          id="InquiryForm.noTransactionRightsError"
          values={{
            NoAccessLink: msg => (
              <NamedLink
                name="NoAccessPage"
                params={{ missingAccessRight: NO_ACCESS_PAGE_INITIATE_TRANSACTIONS }}
              >
                {msg}
              </NamedLink>
            ),
          }}
        />
      ) : (
        <FormattedMessage id="InquiryForm.sendInquiryError" />
      )}
    </p>
  ) : null;
};

/**
 * The InquiryForm component.
 * NOTE: this InquiryForm is only for booking & purchase processes
 * The default-inquiry process is handled differently
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} [props.submitButtonWrapperClassName] - Custom class to be passed for the submit button wrapper
 * @param {boolean} [props.inProgress] - Whether the inquiry is in progress
 * @param {string} props.listingTitle - The listing title
 * @param {string} props.authorDisplayName - The author display name
 * @param {propTypes.error} props.sendInquiryError - The send inquiry error
 * @returns {JSX.Element} inquiry form component
 */
const InquiryForm = props => {
  const {
    start,
    end,
    timeZone = 'Etc/UTC',
    timeSlots,
    monthlyTimeSlots,
    onFetchTimeSlots,
    dayCountAvailableForBooking,
    ...rest
  } = props;

  const initialValues = useMemo(() => {
    let bookingDates = null;
    if (start && end) {
      const startDate = new Date(start);
      const endDate = new Date(end);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      
      // Since isDaily is true in InquiryForm, FieldDateRangePicker's format method
      // expects the endDate to be exclusive and will subtract 1 day for display.
      // Therefore, we add 1 day here so the correct end date is displayed.
      endDate.setDate(endDate.getDate() + 1);

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (startDate >= today && endDate >= startDate) {
        bookingDates = { startDate, endDate };
      }
    }

    return {
      message: '',
      bookingDates,
    };
  }, [start, end]);

  return (
    <FinalForm
      initialValues={initialValues}
      {...rest}
      render={fieldRenderProps => {
        const {
          rootClassName,
        className,
        submitButtonWrapperClassName,
        formId,
        handleSubmit,
        inProgress = false,
        listingTitle,
        authorDisplayName,
        sendInquiryError,
        onSendVerificationEmail,
        sendVerificationEmailInProgress,
        emailUnverified,
        onRequestToBook,
        isBoardingListing,
        vibeCheckListingExists,
        values,
      } = fieldRenderProps;

      const startDate = values?.bookingDates?.startDate;
      const endDate = values?.bookingDates?.endDate;
      const isDaily = true; // For inquiries, assume daily to allow blocking by days

      const allTimeSlots = useMemo(() => getAllTimeSlots(monthlyTimeSlots || {}), [monthlyTimeSlots]);

      const relevantTimeSlots = startDate && !endDate && allTimeSlots.length > 0
        ? combineConsecutiveTimeSlots(allTimeSlots, startDate)
        : allTimeSlots;

      const isDayBlocked = isDayBlockedFn({
        allTimeSlots: relevantTimeSlots,
        monthlyTimeSlots: monthlyTimeSlots || {},
        isDaily,
        startDate,
        endDate,
        timeZone,
      });

      const isOutsideRange = isOutsideRangeFn(
        relevantTimeSlots,
        monthlyTimeSlots || {},
        startDate,
        endDate,
        'line-item/day',
        dayCountAvailableForBooking,
        timeZone,
        false
      );

      // Note: we let the FieldDateRangePicker manage currentMonth state.
      // But if we wanted to trigger fetches on month change, we map it here:
      const onMonthClick = handleMonthClick(
        getStartOf(new Date(), 'month', timeZone), // dummy currentMonth as FieldDateRangePicker handles its own
        monthlyTimeSlots || {},
        dayCountAvailableForBooking,
        timeZone,
        null, // no listingId needed
        onFetchTimeSlots || (() => {})
      );

      // Disable clear dates logic
      // FieldDateRangePicker doesn't have an explicit disable clear prop without tweaking `useMobileMargins` etc
      // We'll rely on it just rendering.


      const SUSPICIOUS_KEYWORDS = [
        'whatsapp',
        'telegram',
        'instagram',
        'call me',
        'text me',
        'dm me',
        'reach me',
        'contact me',
        'copy this link on your browser',
        'my number is',
        'my phone number is',
        'my whatsapp is',
        'my telegram is',
        'my instagram is',
        'copy this link',
        'copy this url',
        'http',
        'www.',
      ];

      const currentMessage = fieldRenderProps.values?.message?.toLowerCase() || '';
      const hasSuspiciousContent = useMemo(
        () => SUSPICIOUS_KEYWORDS.some(keyword => currentMessage.includes(keyword)),
        [currentMessage]
      );

      const [requestingBook, setRequestingBook] = useState(false);

      const handleRequestToBookClick = async () => {
        if (requestingBook) return;

        setRequestingBook(true);

        try {
          await onRequestToBook();
        } finally {
          setRequestingBook(false);
        }
      };

      const intl = useIntl();

      const messageLabel = intl.formatMessage(
        {
          id: 'InquiryForm.messageLabel',
        },
        { authorDisplayName }
      );
      const messagePlaceholder = intl.formatMessage(
        {
          id: 'InquiryForm.messagePlaceholder',
        },
        { authorDisplayName }
      );
      const messageRequiredMessage = intl.formatMessage({
        id: 'InquiryForm.messageRequired',
      });
      const messageRequired = validators.requiredAndNonEmptyString(messageRequiredMessage);

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      const submitDisabled = submitInProgress;

      const onSendVerificationEmailMaybe = () => {
        if (sendVerificationEmailInProgress) return;

        onSendVerificationEmail();
        toast.success('Verification email sent!');
      };

      return (
        <Form className={classes} onSubmit={handleSubmit} enforcePagePreloadFor="OrderDetailsPage">
          <div className={css.inquiryTopSection}>
            <IconInquiry className={css.icon} />
            <Heading as="h2" rootClassName={css.heading}>
              <FormattedMessage id="InquiryForm.heading" values={{ listingTitle }} />
            </Heading>
          </div>

          {emailUnverified ? (
            <div className={css.emailUnverifiedMessage}>
              <div className={css.unverifiedMessageWrapper}>
                <div>
                  Please verify your email address. Without verification, you may not receive
                  messages or responses from other users.&nbsp;
                </div>
              </div>
              <span
                onClick={onSendVerificationEmailMaybe}
                className={sendVerificationEmailInProgress ? css.disabledLink : css.link}
              >
                {sendVerificationEmailInProgress ? 'Sending…' : 'Resend verification email'}
              </span>
            </div>
          ) : null}

          <div style={{ marginBottom: '24px' }}>
            <FieldDateRangePicker
              name="bookingDates"
              className={css.paymentDatePicker}
              // theme="light"
              timeZone={timeZone}
              isDaily={isDaily}
              startDateId={`${formId || 'InquiryForm'}.bookingStartDate`}
              startDateLabel="Selected start date"
              startDatePlaceholderText="Start Date"
              endDateId={`${formId || 'InquiryForm'}.bookingEndDate`}
              endDateLabel="Selected end date"
              endDatePlaceholderText="End Date"
              format={v => {
                const { startDate, endDate } = v || {};
                const formattedStart = startDate
                  ? timeOfDayFromTimeZoneToLocal(startDate, timeZone)
                  : startDate;
                const endDateForPicker =
                  isDaily && endDate ? getInclusiveEndDate(endDate, timeZone) : endDate;
                const formattedEnd = endDateForPicker
                  ? timeOfDayFromTimeZoneToLocal(endDateForPicker, timeZone)
                  : endDateForPicker;
                return v ? { startDate: formattedStart, endDate: formattedEnd } : v;
              }}
              parse={v => {
                const { startDate, endDate } = v || {};
                return v ? getStartAndEndOnTimeZone(startDate, endDate, isDaily, timeZone) : v;
              }}
              useMobileMargins
              isModal={true}
              isDayBlocked={isDayBlocked}
              isOutsideRange={isOutsideRange}
              isBlockedBetween={isBlockedBetween(relevantTimeSlots, timeZone)}
              onMonthClick={onMonthClick}
            />
          </div>

          <FieldTextInput
            className={css.field}
            type="textarea"
            name="message"
            id={formId ? `${formId}.message` : 'message'}
            label={messageLabel}
            placeholder={messagePlaceholder}
            validate={messageRequired}
          />
          {hasSuspiciousContent ? (
            <div className={css.spamWarningCard}>
              <IoInformationCircleOutline className={css.warningIcon} />
              <span>
                Please avoid sharing personal contact details outside Purfurry before booking
                confirmation.
              </span>
            </div>
          ) : null}

          <div className={css.submitButtonContainer}>
            <div className={submitButtonWrapperClassName}>
              <ErrorMessage error={sendInquiryError} />
              <PrimaryButton
                type="submit"
                inProgress={submitInProgress}
                className={css.inquirySubmitButton}
                disabled={submitDisabled}
              >
                <FormattedMessage id="InquiryForm.submitButtonText" />
              </PrimaryButton>
            </div>
            {isBoardingListing && vibeCheckListingExists && (
              <>
                <div className={css.orDivider}>
                  <span>OR</span>
                </div>
                <div className={submitButtonWrapperClassName}>
                  <PrimaryButton
                    type="button"
                    className={css.requestToBookLink}
                    onClick={handleRequestToBookClick}
                    disabled={requestingBook}
                  >
                    {requestingBook ? 'Loading…' : 'Request Meet-Up'}
                  </PrimaryButton>
                  <div className={css.meetupInfo}>
                    <IoInformationCircleOutline className={css.infoIcon} />
                    <span>
                      Not sure yet? Request a free meet-up to visit the boarder, discuss your pet’s
                      routine, and make sure everything feels right before booking.
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
          
          <div className={css.contactWarningBox}>
            <div className={css.contactWarningContent}>
              <div className={css.contactWarningIcon}>🚫</div>
              <div className={css.contactWarningText}>
                <strong>DO NOT SHARE CONTACT DETAILS</strong>
                <p>
                  Sharing your phone number or any other contact info (including
                  numbers in any form or using workarounds) will lead to an immediate ban and your message will be deleted.
                </p>
              </div>
            </div>
          </div>

          <div className={css.whyStaySection}>
            <h3 className={css.whyStayHeading}>Why stay on Purfurry?</h3>
            <div className={css.featuresGrid}>
              <div className={css.featureItem}>
                <div className={css.featureIcon}><RiDiscountPercentFill /></div>
                <div className={css.featureText}>
                  <strong>Save up to ₹500</strong>
                  <p>Use PURF10 on your first booking.</p>
                </div>
              </div>
              <div className={css.featureItem}>
                <div className={css.featureIcon}><RiDiscountPercentFill /></div>
                <div className={css.featureText}>
                  <strong>Only 2.5% platform fee</strong>
                  <p>No hidden charges. (2.5% + GST)</p>
                </div>
              </div>
              <div className={css.featureItem}>
                <div className={css.featureIcon}><FaShieldAlt /></div>
                <div className={css.featureText}>
                  <strong>Up to ₹5,000 Pet Protection</strong>
                  <p>For eligible bookings.</p>
                </div>
              </div>
              <div className={css.featureItem}>
                <div className={css.featureIcon}><BsPeopleFill /></div>
                <div className={css.featureText}>
                  <strong>Meet & Greet Before You Book</strong>
                  <p>Share details then, with confidence.</p>
                </div>
              </div>
              <div className={css.featureItem}>
                <div className={css.featureIcon}><FaCalendarAlt /></div>
                <div className={css.featureText}>
                  <strong>Tracked & Protected Bookings</strong>
                  <p>Your pet's booking is always monitored and supported.</p>
                </div>
              </div>
            </div>
          </div>

        </Form>
      );
    }}
  />
  );
};

export default InquiryForm;
